import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LogOut, Search, Loader2, Download, Mail, Star, Clock, CalendarClock, Image as ImageIcon,
  ShieldCheck, RefreshCw, Trash2, Plus, ExternalLink, AlertTriangle, XCircle,
} from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useSeo from "../hooks/useSeo";
import { useToast } from "../hooks/use-toast";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STATUSES = ["New", "Contacted", "Estimate Scheduled", "Completed", "Archived"];
const RR_STATUSES = ["Not Sent", "Sent", "Follow-Up Needed", "Completed", "Opted Out", "Closed"];

const btn = "px-4 py-2.5 text-[12px] font-bold uppercase tracking-wider transition-colors duration-200";
const btnPri = `${btn} bg-[#244aa2] text-white hover:bg-[#0c2a66]`;
const btnSec = `${btn} border border-[#244aa2] text-[#244aa2] hover:bg-[#e8eefb]`;
const btnDanger = `${btn} border border-[#a10000] text-[#a10000] hover:bg-[#fdeaea]`;

function Card({ title, value, tone = "blue" }) {
  const tones = { blue: "text-[#244aa2]", red: "text-[#a10000]", grey: "text-[#575757]" };
  return (
    <div className="border border-neutral-200 bg-white p-5">
      <p className="text-[11px] font-bold uppercase tracking-widest text-neutral-500">{title}</p>
      <p className={`font-head mt-1 text-3xl font-extrabold ${tones[tone]}`}>{value}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ sign-in */
function SignIn() {
  const { login } = useAuth();
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-6 py-16">
      <div className="w-full max-w-md border border-neutral-200 bg-white p-9 text-center shadow-sm">
        <ShieldCheck className="mx-auto h-10 w-10 text-[#244aa2]" />
        <h1 className="mr-h1 mt-5 text-2xl">Owner Dashboard</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-[#575757]">
          This area is private. Sign in with the authorised Mr.&nbsp;Resurface Google account to view
          estimate requests, customer photos and review requests.
        </p>
        <button onClick={login} data-testid="dashboard-google-signin-button" className={`${btnPri} mt-7 w-full py-3.5`}>
          Sign in with Google
        </button>
        <p className="mt-4 text-[12px] text-neutral-500">
          Access is limited to allow-listed e-mail addresses. This page is excluded from search
          engines.
        </p>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- estimates */
function Estimates() {
  const { toast } = useToast();
  const [data, setData] = useState({ estimates: [], total: 0, status_counts: {} });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const [composerFor, setComposerFor] = useState(null);
  const [reply, setReply] = useState({ subject: "Re: Your MrResurface Estimate Request", body: "" });
  const [replyError, setReplyError] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/estimates", { params: { q: q || undefined, status } });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const patch = async (id, fn) => {
    try {
      const { data: updated } = await fn();
      setData((p) => ({ ...p, estimates: p.estimates.map((e) => (e.id === id ? updated : e)) }));
      return updated;
    } catch (e) {
      toast({ title: "Action failed", description: e?.response?.data?.detail || "Please try again." });
      throw e;
    }
  };

  const sendRequest = async (id) => {
    try {
      const { data: res } = await api.post(`/admin/estimates/${id}/review-request`, {
        channel: "email",
      });
      setData((p) => ({ ...p, estimates: p.estimates.map((e) => (e.id === id ? res.estimate : e)) }));
      toast({
        title: res.delivery === "sent" ? "Review request sent" : "Review request recorded",
        description:
          res.delivery === "sent"
            ? `${res.messages_remaining} reminder(s) remaining, then the sequence closes for good.`
            : res.delivery_note || "Queued — it will send once the e-mail provider key is added.",
      });
    } catch (e) {
      toast({ title: "Not sent", description: e?.response?.data?.detail || "Please try again." });
    }
  };

  const open = data.estimates.find((e) => e.id === openId);

  const sendReply = async (id) => {
    setReplyError("");
    if (!reply.body.trim()) {
      setReplyError("Please type a message before sending.");
      return;
    }
    setSendingReply(true);
    try {
      const { data: updated } = await api.post(`/admin/estimates/${id}/reply`, reply);
      setData((p) => ({ ...p, estimates: p.estimates.map((e) => (e.id === id ? updated : e)) }));
      setComposerFor(null);
      setReply({ subject: "Re: Your MrResurface Estimate Request", body: "" });
      toast({ title: "Reply sent", description: `Delivered to ${updated.email}.` });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setReplyError(
        (typeof d === "string" && d) ||
          d?.message ||
          "The reply could not be sent. Your message has been kept — please try again."
      );
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {STATUSES.map((s) => (
          <Card key={s} title={s} value={data.status_counts?.[s] ?? 0}
                tone={s === "New" ? "red" : s === "Archived" ? "grey" : "blue"} />
        ))}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, town, reference or message…" className="h-11 pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={load} className={btnSec} aria-label="Refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#244aa2]" /></div>
      ) : data.estimates.length === 0 ? (
        <p className="py-16 text-center text-[#575757]">No estimate requests match this filter yet.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.estimates.map((e) => (
            <li key={e.id} className="border border-neutral-200 bg-white">
              <button
                onClick={() => setOpenId(openId === e.id ? null : e.id)}
                className="flex w-full flex-wrap items-center gap-3 p-4 text-left hover:bg-[#f7f7f7]"
                aria-expanded={openId === e.id}
              >
                <span className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
                  e.status === "New" ? "bg-[#fdeaea] text-[#a10000]" : "bg-[#e8eefb] text-[#1a3f8f]"}`}>
                  {e.status}
                </span>
                <span className="font-head font-extrabold text-[#242424]">{e.name}</span>
                {e.replied && (
                  <span data-testid={`replied-badge-${e.id}`} className="bg-[#eaf7ec] px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-[#1a7f37]">
                    Replied
                  </span>
                )}
                <span className="text-[14px] text-[#575757]">{e.job_location}</span>
                <span className="ml-auto flex items-center gap-3 text-[13px] text-neutral-500">
                  <span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{e.photos.length}</span>
                  <span>{e.reference}</span>
                  <span>{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                </span>
              </button>

              {openId === e.id && open && (
                <div className="border-t border-neutral-200 p-5">
                  <div className="grid gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                      <dl className="grid gap-x-6 gap-y-2 text-[14px] sm:grid-cols-2">
                        {[["Email", <a key="e" href={`mailto:${open.email}`} className="text-[#244aa2] underline">{open.email}</a>],
                          ["Phone", <a key="p" href={`tel:${open.phone}`} className="text-[#244aa2] underline">{open.phone}</a>],
                          ["Job location", open.job_location],
                          ["Received", open.created_at ? new Date(open.created_at).toLocaleString() : "—"]].map(([k, v]) => (
                          <div key={k}>
                            <dt className="text-[11px] font-bold uppercase tracking-widest text-neutral-500">{k}</dt>
                            <dd className="text-[#242424]">{v}</dd>
                          </div>
                        ))}
                      </dl>

                      <h4 className="font-head mt-5 text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">Message</h4>
                      <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-[#575757]">{open.message}</p>

                      {/* ---- reply by e-mail ---- */}
                      <div className="mt-6 border border-neutral-200 p-4" data-testid="reply-section">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <h4 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">
                            Communication history ({open.replies?.length || 0})
                          </h4>
                          <button
                            type="button"
                            data-testid="reply-by-email-button"
                            onClick={() => {
                              setReplyError("");
                              setComposerFor(composerFor === open.id ? null : open.id);
                            }}
                            className={btnPri}
                          >
                            <Mail className="mr-1 inline h-4 w-4" /> Reply by Email
                          </button>
                        </div>

                        {composerFor === open.id && (
                          <div className="mt-4 space-y-2 bg-[#f7f7f7] p-4" data-testid="reply-composer">
                            <p className="text-[13px] text-[#575757]">
                              <span className="font-bold uppercase tracking-wider text-neutral-500">To:</span>{" "}
                              <span data-testid="reply-to-address" className="font-semibold text-[#242424]">{open.email}</span>
                            </p>
                            <Input
                              data-testid="reply-subject-input"
                              className="h-11"
                              value={reply.subject}
                              onChange={(ev) => setReply((r) => ({ ...r, subject: ev.target.value }))}
                              placeholder="Subject"
                            />
                            <Textarea
                              data-testid="reply-body-input"
                              className="h-40 min-h-[160px]"
                              value={reply.body}
                              onChange={(ev) => setReply((r) => ({ ...r, body: ev.target.value }))}
                              placeholder="Type your reply to the customer…"
                            />
                            {replyError && (
                              <p role="alert" data-testid="reply-error" className="border-l-4 border-[#a10000] bg-[#fdeaea] px-3 py-2 text-[13px] font-semibold text-[#a10000]">
                                {replyError}
                              </p>
                            )}
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                data-testid="reply-send-button"
                                disabled={sendingReply}
                                onClick={() => sendReply(open.id)}
                                className={`${btnPri} disabled:opacity-60`}
                              >
                                {sendingReply ? "Sending…" : "Send Email"}
                              </button>
                              <button type="button" data-testid="reply-cancel-button" onClick={() => setComposerFor(null)} className={btnSec}>
                                Close
                              </button>
                            </div>
                          </div>
                        )}

                        {open.replies?.length ? (
                          <ul className="mt-4 space-y-2" data-testid="reply-history">
                            {open.replies.map((r) => (
                              <li key={r.id} className="border-l-2 border-[#244aa2] bg-[#f7f7f7] p-3">
                                <p className="text-[12px] font-bold uppercase tracking-wider text-[#244aa2]">
                                  Email sent · {r.at ? new Date(r.at).toLocaleString() : ""}
                                </p>
                                <p className="mt-1 text-[12.5px] text-neutral-500">
                                  To {r.to} · {r.subject}{r.by ? ` · sent by ${r.by}` : ""}
                                </p>
                                <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed text-[#575757]">{r.body}</p>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-3 text-[13px] text-neutral-500">
                            No e-mail replies sent yet for this submission.
                          </p>
                        )}
                      </div>

                      <h4 className="font-head mt-6 text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">
                        Photos ({open.photos.length}) — private
                      </h4>
                      <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-4">
                        {open.photos.map((p) => (
                          <a key={p.photo_id} href={`${API}${p.url.replace("/api", "")}`} target="_blank" rel="noreferrer"
                             className="group block aspect-square overflow-hidden border border-neutral-200">
                            <img src={`${API}${p.url.replace("/api", "")}`} alt={p.original_name}
                                 className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" />
                          </a>
                        ))}
                      </div>

                      <h4 className="font-head mt-6 text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">Private notes</h4>
                      <ul className="mt-2 space-y-2">
                        {open.notes.map((n) => (
                          <li key={n.id} className="flex items-start gap-3 bg-[#f7f7f7] p-3 text-[14px]">
                            <span className="flex-1 whitespace-pre-wrap text-[#575757]">{n.body}</span>
                            <span className="shrink-0 text-[11px] text-neutral-400">
                              {n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}
                            </span>
                            <button aria-label="Delete note"
                                    onClick={() => patch(open.id, () => api.delete(`/admin/estimates/${open.id}/notes/${n.id}`))}>
                              <Trash2 className="h-4 w-4 text-[#a10000]" />
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 flex gap-2">
                        <Textarea value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Add a private note…" className="h-20 min-h-[80px]" />
                        <button className={btnPri}
                                onClick={async () => {
                                  if (!note.trim()) return;
                                  await patch(open.id, () => api.post(`/admin/estimates/${open.id}/notes`, { body: note }));
                                  setNote("");
                                }}>
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <aside className="space-y-5">
                      <div>
                        <h4 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">Status</h4>
                        <Select value={open.status}
                                onValueChange={(v) => patch(open.id, () => api.patch(`/admin/estimates/${open.id}/status`, { status: v }))}>
                          <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <button
                          data-testid="delete-estimate-button"
                          onClick={async () => {
                            if (!window.confirm(`Permanently delete ${open.reference}? This cannot be undone.`)) return;
                            await api.delete(`/admin/estimates/${open.id}`);
                            setOpenId(null);
                            load();
                          }}
                          className="mt-3 flex w-full items-center justify-center gap-2 border-2 border-[#a10000] px-4 py-2.5 text-[12px] font-bold uppercase tracking-wider text-[#a10000] transition-colors hover:bg-[#a10000] hover:text-white"
                        >
                          <Trash2 className="h-4 w-4" /> Delete this request
                        </button>
                      </div>

                      <div className="border border-neutral-200 p-4">
                        <h4 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">Review request</h4>
                        <p className="mt-2 text-[14px] font-bold text-[#244aa2]">{open.review_request?.status || "Not Sent"}</p>
                        <p className="text-[12px] text-neutral-500">
                          {open.review_request?.messages_sent || 0} of 2 messages sent
                          {open.review_request?.closed ? " · sequence closed permanently" : ""}
                          {open.review_request?.opted_out ? " · customer opted out" : ""}
                        </p>
                        <ul className="mt-2 space-y-1 text-[12px] text-neutral-500">
                          {(open.review_request?.history || []).map((h, i) => (
                            <li key={i}>
                              {h.at ? new Date(h.at).toLocaleString() : ""} · {h.channel}
                              {h.follow_up ? " (follow-up)" : " (initial)"} · {h.delivery}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-3 space-y-2">
                          <button onClick={() => sendRequest(open.id)}
                                  disabled={open.review_request?.closed || open.review_request?.opted_out}
                                  className={`${btnPri} w-full disabled:cursor-not-allowed disabled:opacity-50`}>
                            <Mail className="mr-1 inline h-4 w-4" />
                            {open.review_request?.messages_sent ? "Send follow-up" : "Send review request"}
                          </button>
                          <Select value={open.review_request?.status || "Not Sent"}
                                  onValueChange={(v) => patch(open.id, () => api.patch(`/admin/estimates/${open.id}/review-request`, { status: v }))}>
                            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                            <SelectContent>{RR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                          <button onClick={() => patch(open.id, () => api.post(`/admin/estimates/${open.id}/review-request/cancel`))}
                                  className={`${btnDanger} w-full`}>
                            <XCircle className="mr-1 inline h-4 w-4" /> Cancel pending reminder
                          </button>
                        </div>
                      </div>
                    </aside>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- reviews */
function PendingReviews({ onChange }) {
  const { toast } = useToast();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reviews/pending");
      setPending(data.reviews);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/reviews/${id}/${action}`);
      setPending((p) => p.filter((r) => r.id !== id));
      toast({
        title: action === "approve" ? "Review approved" : "Review rejected",
        description: action === "approve"
          ? "It is now live on the Review Wall."
          : "It will never appear publicly.",
      });
      onChange?.();
    } catch (e) {
      toast({ title: "Action failed", description: e?.response?.data?.detail || "Please try again." });
    }
  };

  if (loading) return null;

  return (
    <div className="mb-7 border-l-4 border-[#a10000] bg-white p-5" data-testid="pending-reviews-block">
      <h3 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-[#a10000]">
        Pending reviews from the website ({pending.length})
      </h3>
      {pending.length === 0 ? (
        <p className="mt-2 text-[13px] text-neutral-500">
          Nothing waiting. Reviews submitted through mrresurface.com appear here for approval before
          they go public.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {pending.map((r) => (
            <li key={r.id} data-testid={`pending-review-${r.id}`} className="border border-neutral-200 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-head font-extrabold text-[#242424]">{r.author}</span>
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className={`h-3.5 w-3.5 ${i <= r.rating ? "fill-[#f0a92b] text-[#f0a92b]" : "text-neutral-300"}`} />
                  ))}
                </span>
                <span className="text-[12px] text-neutral-400">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                  {r.submitter_email ? ` · ${r.submitter_email}` : ""}
                </span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed text-[#575757]">{r.content}</p>
              <div className="mt-3 flex gap-2">
                <button data-testid={`approve-review-${r.id}`} className={btnPri} onClick={() => moderate(r.id, "approve")}>
                  Approve &amp; publish
                </button>
                <button data-testid={`reject-review-${r.id}`} className={btnDanger} onClick={() => moderate(r.id, "reject")}>
                  Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Reviews() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [loading, setLoading] = useState(true);
  const [gbp, setGbp] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [form, setForm] = useState({ author: "", content: "", rating: 5, source: "testimonial" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reviews", { params: { q: q || undefined, source, limit: 200 } });
      setItems(data.reviews);
    } finally {
      setLoading(false);
    }
  }, [q, source]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/admin/integrations/google-business").then(({ data }) => setGbp(data)).catch(() => {}); }, []);

  return (
    <div>
      <PendingReviews onChange={load} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="border border-neutral-200 bg-white p-5 lg:col-span-2">
          <h3 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">Google Business Profile sync</h3>
          <p className={`mt-2 text-[14px] font-bold ${gbp?.connected ? "text-[#1a7f37]" : "text-[#a10000]"}`}>
            {gbp ? (gbp.connected ? "Connected — automatic sync active" : "Not connected — credentials required") : "Checking…"}
          </p>
          {gbp && !gbp.connected && (
            <p className="mt-1 text-[12px] text-neutral-500">
              Missing: {Object.entries(gbp.credentials_present).filter(([, v]) => !v).map(([k]) => k).join(", ")}.
              See <code className="bg-neutral-100 px-1">/app/GOOGLE_BUSINESS_PROFILE_SETUP.md</code>
            </p>
          )}
          {gbp && (
            <div className="mt-2 space-y-0.5 text-[12px] text-neutral-500" data-testid="gbp-sync-status">
              <p>{gbp.schedule}</p>
              {gbp.last_sync?.at ? (
                <>
                  <p>
                    Last sync {new Date(gbp.last_sync.at).toLocaleString()}
                    {gbp.last_sync.error ? " — failed" : ` — ${gbp.last_sync.inserted ?? 0} new review${gbp.last_sync.inserted === 1 ? "" : "s"} imported`}
                  </p>
                  {gbp.last_sync.error && (
                    <p className="font-semibold text-[#a10000]" data-testid="gbp-sync-error">
                      {gbp.last_sync.error}
                    </p>
                  )}
                  {gbp.last_sync.last_success_at && (
                    <p>Last successful sync {new Date(gbp.last_sync.last_success_at).toLocaleString()}</p>
                  )}
                </>
              ) : (
                <p>No Google sync has run yet.</p>
              )}
              <p>{gbp.google_reviews_stored} Google reviews stored · {gbp.google_reviews_linked} matched to a Google review ID</p>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button className={btnSec} data-testid="gbp-connect-button"
                    onClick={async () => {
                      try {
                        const { data } = await api.get("/admin/integrations/google-business/authorize");
                        window.open(data.auth_url, "_blank", "noopener");
                      } catch (e) {
                        toast({ title: "Cannot start Google connection", description: e?.response?.data?.detail?.message || "Add the Google client ID and secret first." });
                      }
                    }}>
              Connect Google account
            </button>
            <button className={btnSec} data-testid="gbp-reconcile-button" disabled={reconBusy}
                    onClick={async () => {
                      setReconBusy(true);
                      try {
                        const { data } = await api.post("/admin/integrations/google-business/reconcile");
                        setRecon(data.summary);
                        toast({ title: "Duplicate check complete", description: "Dry run only — nothing was changed." });
                      } catch (e) {
                        setRecon(null);
                        toast({ title: "Duplicate check unavailable", description: e?.response?.data?.detail?.message || "Connect Google first." });
                      } finally {
                        setReconBusy(false);
                      }
                    }}>
              {reconBusy ? "Checking…" : "Check for duplicates"}
            </button>
            <button className={btnSec} data-testid="gbp-sync-button"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/admin/integrations/google-business/sync");
                        toast({ title: "Google sync complete", description: `${data.inserted} new, ${data.linked} linked to a Google ID, ${data.replies_updated} replies updated.` });
                        load();
                        api.get("/admin/integrations/google-business").then(({ data: g }) => setGbp(g)).catch(() => {});
                      } catch (e) {
                        toast({ title: "Google sync unavailable", description: e?.response?.data?.detail?.message || "Credentials required." });
                        api.get("/admin/integrations/google-business").then(({ data: g }) => setGbp(g)).catch(() => {});
                      }
                    }}>
              Sync Google reviews
            </button>
            <button className={btnSec}
                    onClick={async () => {
                      const { data } = await api.post("/admin/reviews/import-broadly");
                      toast({ title: "Broadly import complete", description: `${data.inserted} new, ${data.skipped_duplicates} duplicates skipped, ${data.total_reviews} total.` });
                      load();
                    }}>
              Re-import Broadly archive
            </button>
          </div>
          {recon && (
            <div className="mt-4 border border-neutral-200 bg-neutral-50 p-4" data-testid="gbp-reconcile-results">
              <p className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">
                Duplicate check — dry run (no changes made)
              </p>
              <dl className="mt-2 divide-y divide-neutral-200 text-[13px]">
                {[
                  ["Live Google reviews returned", recon.live_google_reviews, "gbp-recon-live"],
                  ["Google archive rows stored", recon.stored_google_records, "gbp-recon-stored"],
                  ["Already matched to a Google ID", recon.already_matched, "gbp-recon-already"],
                  ["Confirmed high-confidence matches", recon.newly_matched_confirmed, "gbp-recon-confirmed"],
                  ["Probable — needs manual review", recon.probable_needs_review, "gbp-recon-probable"],
                  ["Unmatched archive rows", recon.remaining_unmatched_stored, "gbp-recon-unmatched-stored"],
                  ["Live reviews with no stored copy", recon.live_reviews_with_no_stored_copy, "gbp-recon-unmatched-live"],
                  ["Duplicate rows that would be hidden", recon.duplicates_currently_public, "gbp-recon-duplicates"],
                  ["Public reviews now", recon.public_reviews_now, "gbp-recon-public-now"],
                  ["Projected public reviews after cleanup", recon.public_reviews_after_cleanup, "gbp-recon-public-after"],
                ].map(([label, value, tid]) => (
                  <div key={tid} className="flex items-baseline justify-between gap-4 py-1.5">
                    <dt className="text-neutral-600">{label}</dt>
                    <dd className="font-head font-extrabold text-[#0c2a66]" data-testid={tid}>{value ?? 0}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-2 text-[12px] text-neutral-500">
                Nothing was modified, hidden, merged, relinked or deleted. Cleanup is a separate action.
              </p>
              <button className={`${btnPri} mt-3`} data-testid="gbp-apply-cleanup-button"
                      disabled={reconBusy || !recon.newly_matched_confirmed}
                      onClick={() => setShowApply(true)}>
                Apply cleanup
              </button>
            </div>
          )}
          <AlertDialog open={showApply} onOpenChange={setShowApply}>
            <AlertDialogContent data-testid="gbp-apply-cleanup-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Apply the duplicate cleanup?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-left text-[13px]">
                    <p>This will only act on the {recon?.newly_matched_confirmed || 0} confirmed high-confidence matches:</p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>Confirmed duplicate rows are hidden and flagged as duplicates — never deleted.</li>
                      <li>The older archive row stays the primary public record.</li>
                      <li>The real Google review ID is attached to that older archive row.</li>
                      <li>Existing text, rating, date, source, label, featured status and any stored reply are preserved.</li>
                      <li>Probable or uncertain matches are not changed automatically.</li>
                    </ul>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="gbp-apply-cleanup-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="gbp-apply-cleanup-confirm"
                                   onClick={async () => {
                                     setReconBusy(true);
                                     try {
                                       const { data } = await api.post("/admin/integrations/google-business/reconcile?apply=true");
                                       const r = data.result || {};
                                       toast({ title: "Cleanup applied", description: `${r.archive_rows_linked || 0} archive rows linked, ${r.duplicate_copies_hidden || 0} duplicates hidden, ${r.public_reviews_now || 0} public reviews now.` });
                                       setRecon(null);
                                       load();
                                       api.get("/admin/integrations/google-business").then(({ data: g }) => setGbp(g)).catch(() => {});
                                     } catch (e) {
                                       toast({ title: "Cleanup failed", description: e?.response?.data?.detail?.message || "Nothing was changed." });
                                     } finally {
                                       setReconBusy(false);
                                     }
                                   }}>
                  Apply cleanup
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <div className="border border-neutral-200 bg-white p-5 lg:col-span-2">
          <h3 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">Add a testimonial manually</h3>
          <div className="mt-2 space-y-2">
            <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Customer name & town" className="h-10" />
            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Review text, in the customer's own words" className="h-20 min-h-[80px]" />
            <div className="flex gap-2">
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
                <SelectTrigger className="h-10 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} star</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="h-10 flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["testimonial", "google", "facebook", "angi", "bbb"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <button className={btnPri}
                      onClick={async () => {
                        try {
                          await api.post("/admin/reviews", form);
                          setForm({ author: "", content: "", rating: 5, source: "testimonial" });
                          toast({ title: "Testimonial added" });
                          load();
                        } catch (e) {
                          toast({ title: "Not added", description: e?.response?.data?.detail || "Check the fields." });
                        }
                      }}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-7 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reviews…" className="h-11 pl-9" />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="h-11 w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {["google", "angi", "facebook", "testimonial", "bbb"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#244aa2]" /></div>
      ) : (
        <ul className="mt-6 space-y-2">
          {items.map((r) => (
            <li key={r.id} className="flex flex-wrap items-start gap-3 border border-neutral-200 bg-white p-4">
              <span className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#e8eefb] text-[#1a3f8f]">{r.source_label}</span>
              <span className="font-head font-extrabold text-[#242424]">{r.author}</span>
              <span className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-3.5 w-3.5 ${i <= r.rating ? "fill-[#f0a92b] text-[#f0a92b]" : "text-neutral-300"}`} />)}
              </span>
              <p className="w-full text-[14px] leading-relaxed text-[#575757]">{r.content}</p>
              <div className="ml-auto flex items-center gap-3">
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-[#244aa2]"><ExternalLink className="h-4 w-4" /></a>}
                <label className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-neutral-500">
                  Published
                  <Switch checked={r.published}
                          onCheckedChange={async (v) => {
                            await api.patch(`/admin/reviews/${r.id}`, { published: v });
                            setItems((p) => p.map((x) => (x.id === r.id ? { ...x, published: v } : x)));
                          }} />
                </label>
                <label className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-neutral-500">
                  Featured
                  <Switch checked={r.featured}
                          onCheckedChange={async (v) => {
                            await api.patch(`/admin/reviews/${r.id}`, { featured: v });
                            setItems((p) => p.map((x) => (x.id === r.id ? { ...x, featured: v } : x)));
                          }} />
                </label>
                <button aria-label="Delete review"
                        onClick={async () => { await api.delete(`/admin/reviews/${r.id}`); load(); }}>
                  <Trash2 className="h-4 w-4 text-[#a10000]" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* --------------------------------------------------- review requests (native) */
function ReviewRequests() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [optouts, setOptouts] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [form, setForm] = useState({ name: "", email: "", phone: "", job_note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: d }, { data: o }] = await Promise.all([
      api.get("/admin/review-requests", { params: { q: q || undefined, status } }),
      api.get("/admin/review-optouts"),
    ]);
    setData(d);
    setOptouts(o.optouts);
  }, [q, status]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const act = async (fn, okTitle) => {
    try {
      await fn();
      toast({ title: okTitle });
      load();
    } catch (e) {
      toast({ title: "Action failed", description: e?.response?.data?.detail || "Please try again." });
    }
  };

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast({ title: "Name and e-mail are required" });
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/review-requests", form);
      setForm({ name: "", email: "", phone: "", job_note: "" });
      toast({ title: "Review request sent", description: "Google review invite e-mailed. One reminder follows automatically." });
      load();
    } catch (e) {
      toast({ title: "Not sent", description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#244aa2]" /></div>;

  return (
    <div data-testid="review-requests-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card title="Open" value={(data.status_counts?.Sent || 0) + (data.status_counts?.["Follow-Up Needed"] || 0)} />
        <Card title="Completed" value={data.status_counts?.Completed || 0} />
        <Card title="Closed" value={data.status_counts?.Closed || 0} tone="grey" />
        <Card title="Opted out" value={data.suppressed_count || 0} tone="red" />
      </div>

      <div className="mt-7 border border-neutral-200 bg-white p-5">
        <h3 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">
          Send a new review request
        </h3>
        <p className="mt-1 text-[12.5px] text-neutral-500">
          Google is the primary button in the e-mail. One automatic reminder follows after{" "}
          {data.reminder_delay_days} days, then the request closes permanently
          (maximum {data.max_messages} messages, ever).
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          <Input data-testid="rr-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Customer name" className="h-11" />
          <Input data-testid="rr-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Customer e-mail" className="h-11" />
          <Input data-testid="rr-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="h-11" />
          <Input data-testid="rr-note-input" value={form.job_note} onChange={(e) => setForm({ ...form, job_note: e.target.value })} placeholder="Job note (optional)" className="h-11" />
        </div>
        <button data-testid="rr-send-button" onClick={create} disabled={busy} className={`${btnPri} mt-3 disabled:opacity-60`}>
          <Mail className="mr-1 inline h-4 w-4" /> {busy ? "Sending…" : "Send review request"}
        </button>
      </div>

      <div className="mt-7 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or e-mail…" className="h-11 pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {RR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={load} className={btnSec} aria-label="Refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>

      {data.requests.length === 0 ? (
        <p className="py-14 text-center text-[#575757]">No review requests match this filter yet.</p>
      ) : (
        <ul className="mt-6 space-y-2">
          {data.requests.map((r) => (
            <li key={r.id} data-testid={`review-request-${r.id}`} className="border border-neutral-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
                  r.opted_out ? "bg-[#fdeaea] text-[#a10000]"
                    : r.status === "Completed" ? "bg-[#eaf7ec] text-[#1a7f37]"
                    : "bg-[#e8eefb] text-[#1a3f8f]"}`}>
                  {r.status}
                </span>
                <span className="font-head font-extrabold text-[#242424]">{r.name}</span>
                <span className="text-[13.5px] text-[#575757]">{r.email}</span>
                <span className="ml-auto text-[12px] text-neutral-500">
                  {r.messages_sent} of {data.max_messages} messages
                  {r.reminder_sent ? " · reminder sent" : r.reminder_due_at ? ` · reminder due ${new Date(r.reminder_due_at).toLocaleDateString()}` : ""}
                  {r.closed ? " · closed permanently" : ""}
                  {r.review_id ? " · review received" : ""}
                </span>
              </div>
              <ul className="mt-2 space-y-1 text-[12px] text-neutral-500">
                {r.history.map((h, i) => (
                  <li key={i}>
                    {h.at ? new Date(h.at).toLocaleString() : ""} · {h.channel}
                    {h.follow_up ? " (reminder)" : " (initial)"} · {h.delivery}
                    {h.by ? ` · ${h.by}` : ""}
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  data-testid={`rr-send-now-${r.id}`}
                  disabled={r.closed || r.opted_out || r.messages_sent >= data.max_messages}
                  onClick={() => act(() => api.post(`/admin/review-requests/${r.id}/send`), "Reminder sent")}
                  className={`${btnSec} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  Send reminder now
                </button>
                <Select value={r.status} onValueChange={(v) => act(() => api.patch(`/admin/review-requests/${r.id}`, { status: v }), "Status updated")}>
                  <SelectTrigger className="h-10 w-[190px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{RR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <button
                  data-testid={`rr-cancel-${r.id}`}
                  disabled={r.closed}
                  onClick={() => act(() => api.post(`/admin/review-requests/${r.id}/cancel`), "Request closed")}
                  className={`${btnDanger} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  <XCircle className="mr-1 inline h-4 w-4" /> Cancel
                </button>
                <a href={r.review_url} target="_blank" rel="noreferrer" className="text-[12px] font-bold uppercase tracking-wider text-[#244aa2] underline">
                  Open customer link
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-8 border border-neutral-200 bg-white p-5" data-testid="optout-list">
        <h3 className="font-head text-[12px] font-extrabold uppercase tracking-widest text-neutral-500">
          Opted out of review requests ({optouts.length})
        </h3>
        {optouts.length === 0 ? (
          <p className="mt-2 text-[13px] text-neutral-500">Nobody has opted out.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-[13px] text-[#575757]">
            {optouts.map((o) => (
              <li key={o.email} className="flex items-center gap-3">
                <span>{o.email}</span>
                <span className="text-[11px] text-neutral-400">
                  {o.at ? new Date(o.at).toLocaleDateString() : ""} · {o.source}
                </span>
                <button
                  className="text-[11px] font-bold uppercase tracking-wider text-[#a10000] underline"
                  onClick={() => act(() => api.delete(`/admin/review-optouts/${encodeURIComponent(o.email)}`), "Suppression removed")}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- settings */
function Settings() {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [queue, setQueue] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/settings").then(({ data }) => setS(data));
    api.get("/admin/email-queue").then(({ data }) => setQueue(data));
  }, []);
  useEffect(load, [load]);

  if (!s) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#244aa2]" /></div>;

  const save = async () => {
    const { data } = await api.put("/admin/settings", {
      hours: s.hours, closure: s.closure, notify_email: s.notify_email,
    });
    setS(data);
    toast({ title: "Settings saved" });
  };

  return (
    <div className="grid gap-7 lg:grid-cols-2">
      <div className="border border-neutral-200 bg-white p-6">
        <h3 className="mr-h3 flex items-center gap-2 text-lg"><Clock className="h-5 w-5 text-[#a10000]" /> Business hours</h3>
        <div className="mt-4 space-y-2">
          {s.hours.map((h, i) => (
            <div key={h.day} className="flex items-center gap-3">
              <span className="w-12 text-[13px] font-bold text-[#242424]">{h.day}</span>
              <Input value={h.value} className="h-10 flex-1"
                     onChange={(e) => {
                       const hours = [...s.hours];
                       hours[i] = { ...h, value: e.target.value, closed: /closed/i.test(e.target.value) };
                       setS({ ...s, hours });
                     }} />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-neutral-200 bg-white p-6">
        <h3 className="mr-h3 flex items-center gap-2 text-lg"><CalendarClock className="h-5 w-5 text-[#a10000]" /> Vacation / closure notice</h3>
        <label className="mt-4 flex items-center gap-3 text-[13px] font-bold uppercase tracking-wider text-neutral-600">
          <Switch checked={!!s.closure.active} onCheckedChange={(v) => setS({ ...s, closure: { ...s.closure, active: v } })} />
          Show notice on every page
        </label>
        <Input className="mt-3 h-10" placeholder="Title, e.g. Closed for vacation" value={s.closure.title || ""}
               onChange={(e) => setS({ ...s, closure: { ...s.closure, title: e.target.value } })} />
        <Textarea className="mt-2 h-20 min-h-[80px]" placeholder="Message shown to visitors" value={s.closure.message || ""}
                  onChange={(e) => setS({ ...s, closure: { ...s.closure, message: e.target.value } })} />
        <Input className="mt-2 h-10" type="date" value={(s.closure.until || "").slice(0, 10)}
               onChange={(e) => setS({ ...s, closure: { ...s.closure, until: e.target.value || null } })} />
      </div>

      <div className="border border-neutral-200 bg-white p-6">
        <h3 className="mr-h3 flex items-center gap-2 text-lg"><Mail className="h-5 w-5 text-[#a10000]" /> Notifications</h3>
        <p className="mt-2 text-[13px] text-neutral-500">New estimate requests are e-mailed to this address.</p>
        <Input className="mt-3 h-10" type="email" value={s.notify_email || ""}
               onChange={(e) => setS({ ...s, notify_email: e.target.value })} />
        <div className={`mt-4 flex items-start gap-2 p-3 text-[13px] ${s.email_provider.configured ? "bg-[#eaf7ec] text-[#1a7f37]" : "bg-[#fdeaea] text-[#a10000]"}`}>
          {s.email_provider.configured ? <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>
            {s.email_provider.name} {s.email_provider.configured ? "is configured — e-mail is live." : "API key is not set. Requests are still saved and queued; nothing is lost."}
          </span>
        </div>
        {queue && (
          <div className="mt-4">
            <p className="text-[13px] font-bold text-[#242424]">Queue: {queue.pending} pending</p>
            <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-[12px] text-neutral-500">
              {queue.messages.slice(0, 12).map((m) => (
                <li key={m.id}>{m.status} · {m.kind} · {m.to}</li>
              ))}
            </ul>
            <button className={`${btnSec} mt-3`} onClick={async () => {
              const { data } = await api.post("/admin/email-queue/flush");
              toast({ title: "Queue flushed", description: `${data.sent} sent, ${data.pending} still pending.` });
              load();
            }}>Retry queued e-mail</button>
          </div>
        )}
      </div>

      <div className="border border-neutral-200 bg-white p-6">
        <h3 className="mr-h3 flex items-center gap-2 text-lg"><Download className="h-5 w-5 text-[#a10000]" /> QR codes</h3>
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          {[
            { kind: "payment", title: "Secure payment", note: "For invoices, estimates, cards, handouts and signage." },
            { kind: "google-review", title: "Google review", note: "For post-job review requests and printed handouts." },
          ].map((x) => (
            <div key={x.kind} className="text-center">
              <img src={`${API}/qr/${x.kind}.png?size=8`} alt={`${x.title} QR code`} className="mx-auto w-[150px] border border-neutral-200" />
              <p className="font-head mt-2 text-[13px] font-extrabold text-[#242424]">{x.title}</p>
              <p className="text-[11px] text-neutral-500">{x.note}</p>
              <a href={`${API}/qr/${x.kind}.png?size=20&download=true`} className={`${btnSec} mt-2 inline-block`}>
                <Download className="mr-1 inline h-3.5 w-3.5" /> Download PNG
              </a>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        <button className={`${btnPri} px-8 py-3.5`} onClick={save}>Save settings</button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- dashboard */
export default function Dashboard() {
  useSeo("Owner Dashboard — Mr. Resurface", "", { noindex: true });
  const { user, loading, logout } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (user?.is_owner) api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [user]);

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#244aa2]" /></div>;
  }
  if (!user) return <SignIn />;
  if (!user.is_owner) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6">
        <div className="max-w-md border border-neutral-200 bg-white p-8 text-center">
          <AlertTriangle className="mx-auto h-9 w-9 text-[#a10000]" />
          <h1 className="mr-h1 mt-4 text-xl">Not authorised</h1>
          <p className="mt-3 text-[15px] text-[#575757]">
            <strong>{user.email}</strong> is not on the owner allow-list. Add it to the
            <code className="mx-1 bg-neutral-100 px-1">OWNER_EMAILS</code> environment variable.
          </p>
          <button onClick={logout} className={`${btnSec} mt-6`}>Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f7f7f7] py-10">
      <div className="mr-container-wide">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b-4 border-[#244aa2] pb-5">
          <div>
            <h1 className="mr-h1 text-2xl sm:text-3xl">Owner Dashboard</h1>
            <p className="mt-1 text-[14px] text-[#575757]">
              Signed in as {user.email} · private, excluded from search engines
            </p>
          </div>
          <div className="flex items-center gap-4">
            {stats && (
              <span className="hidden text-[13px] text-neutral-600 sm:block">
                {stats.estimates_total} requests · {stats.reviews_published} published reviews
              </span>
            )}
            <button onClick={logout} className={btnSec}><LogOut className="mr-1 inline h-4 w-4" /> Sign out</button>
          </div>
        </header>

        <Tabs defaultValue="estimates" className="mt-7">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="estimates">Estimate requests</TabsTrigger>
            <TabsTrigger value="requests">Review requests</TabsTrigger>
            <TabsTrigger value="reviews">Reviews &amp; moderation</TabsTrigger>
            <TabsTrigger value="settings">Hours, notices &amp; QR</TabsTrigger>
          </TabsList>
          <TabsContent value="estimates"><Estimates /></TabsContent>
          <TabsContent value="requests"><ReviewRequests /></TabsContent>
          <TabsContent value="reviews"><Reviews /></TabsContent>
          <TabsContent value="settings"><Settings /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
